import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Box,
  Layers,
  Server,
  DollarSign,
  Cpu,
  HardDrive,
  Database,
  RefreshCw,
  Filter,
  Download,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#06b6d4', '#059669', '#0ea5e9', '#f59e0b', '#ef4444', '#06b6d4', '#06b6d4', '#f97316'];

const KubernetesCosts = () => {
  const { shouldShowData } = useDemoData();

  const { token } = useAuth();
  const [namespaces, setNamespaces] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [selectedNamespace, setSelectedNamespace] = useState('all');
  const [loading, setLoading] = useState(true);
  const [totalCost, setTotalCost] = useState(0);
  const [cluster, setCluster] = useState('default');

  useEffect(() => {
    fetchData();
  }, [cluster, selectedNamespace]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch namespace costs
      const nsResponse = await axios.get(`${API_URL}/kubernetes/namespaces?cluster=${cluster}`, { headers });
      setNamespaces(nsResponse.data.namespaces);
      setTotalCost(nsResponse.data.total_cost);
      
      // Fetch workload costs
      const workloadUrl = selectedNamespace !== 'all' 
        ? `${API_URL}/kubernetes/workloads?namespace=${selectedNamespace}`
        : `${API_URL}/kubernetes/workloads`;
      const workloadResponse = await axios.get(workloadUrl, { headers });
      setWorkloads(workloadResponse.data.workloads);
    } catch (error) {
      toast.error('Failed to fetch Kubernetes cost data');
    } finally {
      setLoading(false);
    }
  };

  const pieData = namespaces.map(ns => ({
    name: ns.namespace,
    value: ns.total_cost
  }));

  if (!shouldShowData) {
    return <EmptyDataState title="Kubernetes Costs" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="kubernetes-costs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Box className="w-8 h-8 text-emerald-500" />
            Kubernetes Cost Allocation
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Cost breakdown by namespace and workload
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-cyan-900/30">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total K8s Cost</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  ${totalCost.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-blue-900/30">
                <Layers className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Namespaces</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {namespaces.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Server className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Workloads</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {workloads.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Box className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Pods</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {namespaces.reduce((sum, ns) => sum + ns.pod_count, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Namespace Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Namespace</CardTitle>
            <CardDescription>Distribution of costs across namespaces</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Resource Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Resource Cost Breakdown</CardTitle>
            <CardDescription>CPU, Memory, and Storage costs per namespace</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <BarChart data={namespaces} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(val) => `$${val}`} />
                  <YAxis type="category" dataKey="namespace" width={100} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="cpu_cost" name="CPU" fill="#06b6d4" stackId="a" />
                  <Bar dataKey="memory_cost" name="Memory" fill="#059669" stackId="a" />
                  <Bar dataKey="storage_cost" name="Storage" fill="#0ea5e9" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Namespace Details */}
      <Card>
        <CardHeader>
          <CardTitle>Namespace Details</CardTitle>
          <CardDescription>Detailed breakdown by namespace</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Namespace</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">CPU Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Memory Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Storage Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Total Cost</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Pods</th>
                </tr>
              </thead>
              <tbody>
                {namespaces.map((ns, idx) => (
                  <tr 
                    key={ns.namespace}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => setSelectedNamespace(ns.namespace)}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span className="font-medium text-slate-900 dark:text-white">{ns.namespace}</span>
                        {ns.namespace === 'production' && (
                          <Badge variant="outline" className="text-xs">prod</Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-slate-400 dark:text-slate-400">${ns.cpu_cost.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 text-slate-400 dark:text-slate-400">${ns.memory_cost.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 text-slate-400 dark:text-slate-400">${ns.storage_cost.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 font-medium text-slate-900 dark:text-white">${ns.total_cost.toLocaleString()}</td>
                    <td className="text-center py-3 px-4 text-slate-400 dark:text-slate-400">{ns.pod_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Workloads Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Workload Costs</CardTitle>
            <CardDescription>Cost allocation by deployment and statefulset</CardDescription>
          </div>
          <Select value={selectedNamespace} onValueChange={setSelectedNamespace}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by namespace" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Namespaces</SelectItem>
              {namespaces.map(ns => (
                <SelectItem key={ns.namespace} value={ns.namespace}>{ns.namespace}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Workload</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Kind</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Namespace</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Replicas</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">CPU Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Memory Cost</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {workloads.map((w, idx) => (
                  <tr key={`${w.namespace}-${w.workload}`} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-3 px-4 font-medium text-slate-900 dark:text-white">{w.workload}</td>
                    <td className="py-3 px-4">
                      <Badge variant={w.kind === 'statefulset' ? 'default' : 'outline'}>
                        {w.kind}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-400 dark:text-slate-400">{w.namespace}</td>
                    <td className="text-center py-3 px-4 text-slate-400 dark:text-slate-400">{w.replicas}</td>
                    <td className="text-right py-3 px-4 text-slate-400 dark:text-slate-400">${w.cpu_cost.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 text-slate-400 dark:text-slate-400">${w.memory_cost.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 font-medium text-slate-900 dark:text-white">${w.total_cost.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Demo Notice */}
      <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">Demo Mode</p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Displaying sample Kubernetes cost data. Connect to your Prometheus/Kubecost instance 
                for real-time cluster cost allocation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KubernetesCosts;
